import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import axios, { type AxiosRequestConfig } from 'axios'

const SLICE_NAME = 'captureImages'

const prepConfig = (getState: any): AxiosRequestConfig => {
	const { userLogin } = getState()
	const token = userLogin?.userInfo?.token
	return token ? { headers: { Authorization: `Bearer ${token}` } } : {}
}

const errorMessage = (err: any, fallback: string): string =>
	err?.response?.data?.message || err?.response?.data || err?.message || fallback

export interface CaptureImageRow {
	_id: string
	url: string
	label: string
	capturedAt: string
	deviceId?: string
	usedAt?: string
	name?: string
	size?: number
	/**
	 * The Cloudinary `public_id`, and the format Cloudinary reported.
	 *
	 * The API has always returned both: `captureImageController` has no
	 * projection, so the whole document goes over the wire. This interface
	 * simply never declared them, which meant `FieldRecord` could not pass them
	 * on when pulling an image into a note, and the resulting `locationImage`
	 * row was written without either. See MYD-462.
	 *
	 * `uuid` is the only handle back to the stored asset. Nothing deletes from
	 * Cloudinary today, so losing it costs nothing yet, but a row that cannot
	 * name its own asset cannot take part in any cleanup built later.
	 */
	uuid?: string
	type?: string
}

/** The whole library, newest session first. */
export const initializeCaptureImages = createAsyncThunk(
	`${SLICE_NAME}/initialize`,
	async (params: { deviceId?: string; unused?: boolean } | undefined, { getState, rejectWithValue }) => {
		try {
			const query = new URLSearchParams()
			if (params?.deviceId) query.set('deviceId', params.deviceId)
			if (params?.unused) query.set('unused', 'true')
			const suffix = query.toString() ? `?${query}` : ''

			const { data } = await axios.get(`/api/captureimages${suffix}`, prepConfig(getState))
			return data
		} catch (err) {
			return rejectWithValue(errorMessage(err, 'Could not load your images.'))
		}
	},
)

/** One capture session: a label and the shots taken under it. */
export const addCaptureImages = createAsyncThunk(
	`${SLICE_NAME}/add`,
	async (
		payload: { label: string; deviceId?: string; images: { url: string }[] },
		{ getState, rejectWithValue },
	) => {
		try {
			const { data } = await axios.post('/api/captureimages', payload, prepConfig(getState))
			return data
		} catch (err) {
			return rejectWithValue(errorMessage(err, 'Could not save these images.'))
		}
	},
)

/** Record that images have been pulled into a note. */
export const markCaptureImagesUsed = createAsyncThunk(
	`${SLICE_NAME}/markUsed`,
	async (ids: string[], { getState, rejectWithValue }) => {
		try {
			await axios.put('/api/captureimages/used', { ids }, prepConfig(getState))
			return ids
		} catch (err) {
			return rejectWithValue(errorMessage(err, 'Could not mark these images as used.'))
		}
	},
)

export const deleteCaptureImage = createAsyncThunk(
	`${SLICE_NAME}/delete`,
	async (id: string, { getState, rejectWithValue }) => {
		try {
			await axios.put(`/api/captureimages/${id}/delete`, {}, prepConfig(getState))
			return id
		} catch (err) {
			return rejectWithValue(errorMessage(err, 'Could not remove this image.'))
		}
	},
)

interface CaptureImageState {
	entities: Record<string, CaptureImageRow>
	ids: string[]
	isLoading: boolean
	isInitialized: boolean
	error: string | null
}

const initialState: CaptureImageState = {
	entities: {},
	ids: [],
	isLoading: false,
	isInitialized: false,
	error: null,
}

/** Newest capture first, falling back to insertion order. */
const sortIds = (state: CaptureImageState) => {
	state.ids.sort((a, b) => {
		const left = new Date(state.entities[b]?.capturedAt ?? 0).getTime()
		const right = new Date(state.entities[a]?.capturedAt ?? 0).getTime()
		return left - right
	})
}

const captureImageSlice = createSlice({
	name: SLICE_NAME,
	initialState,
	reducers: {},
	extraReducers: builder => {
		builder
			.addCase(initializeCaptureImages.pending, state => {
				state.isLoading = true
				state.error = null
			})
			.addCase(initializeCaptureImages.fulfilled, (state, action) => {
				const rows: CaptureImageRow[] = (action.payload as any) || []
				state.entities = {}
				state.ids = []
				for (const row of rows) {
					if (!row?._id) continue
					state.entities[row._id] = row
					state.ids.push(row._id)
				}
				state.isLoading = false
				state.isInitialized = true
			})
			.addCase(initializeCaptureImages.rejected, (state, action) => {
				state.isLoading = false
				state.isInitialized = true
				state.error = String(action.payload ?? 'Could not load your images.')
			})

			.addCase(addCaptureImages.fulfilled, (state, action) => {
				const rows: CaptureImageRow[] = (action.payload as any) || []
				for (const row of rows) {
					if (!row?._id) continue
					state.entities[row._id] = row
					if (!state.ids.includes(row._id)) state.ids.push(row._id)
				}
				sortIds(state)
			})

			.addCase(markCaptureImagesUsed.fulfilled, (state, action) => {
				const stamped = new Date().toISOString()
				for (const id of (action.payload as string[]) || []) {
					const row = state.entities[id]
					// First use only, matching the server: a second note must not
					// rewrite when the image first left the library.
					if (row && !row.usedAt) row.usedAt = stamped
				}
			})

			.addCase(deleteCaptureImage.fulfilled, (state, action) => {
				const id = action.payload as string
				delete state.entities[id]
				state.ids = state.ids.filter(candidate => candidate !== id)
			})
	},
})

export default captureImageSlice.reducer
